define('presentation/common/packagePicker/viewModels/packagePickerViewModel',[
    'i18next',
    'businessServices/converters/currencyFormatter',
    'common/promises/promiseFactory',
    'constants/billingPackageTypeConstants',
    'presentation/common/packagePicker/presentationObjects/packagePresentationObject',
    'presentation/tabs/presentationObjects/tabsPresentationObject'
], function () {
    return function () {
        let self = this;

        const CurrencyFormatterConstructor = require('businessServices/converters/currencyFormatter');
        const _currencyFormatter = new CurrencyFormatterConstructor();

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const BillingPackagePresentationObjectConstructor = require('presentation/common/packagePicker/presentationObjects/packagePresentationObject');
        const TabPresentationObjectConstructor = require('presentation/tabs/presentationObjects/tabsPresentationObject');

        const _i18n = require('i18next');
        const _billingPackageTypes = require('constants/billingPackageTypeConstants');

        const GRID_ROWS = {
            price: 'price',
            included: 'included',
            includedChats: 'includedChats',
            includedContacts: 'includedContacts',
            includedMinutes: 'includedMinutes',
            includedNumbers: 'includedNumbers',
            includedScripts: 'includedScripts',
            includedUsers: 'includedUsers',
            extra: 'extra',
            extraChat: 'extraChat',
            extraContact: 'extraContact',
            extraMinute: 'extraMinute',
            extraNumber: 'extraNumber',
            extraScript: 'extraScript',
            extraUser: 'extraUser'
        };

        const TRANSLATION_KEYS = {
            INCLUDED: {
                MINUTES: 'packagePicker:packageFeatures.includedMinutes',
                PHONE_NUMBERS: 'packagePicker:packageFeatures.includedPhoneNumbers',
                USERS: 'packagePicker:packageFeatures.includedUsers',
                CHATS: 'packagePicker:packageFeatures.includedChats',
                CONTACTS: 'packagePicker:packageFeatures.includedContacts',
                SCRIPTS: 'packagePicker:packageFeatures.includedScripts'
            },
            ADDITIONAL: {
                MINUTES: 'packagePicker:packageFeatures.additionalMinutes',
                PHONE_NUMBERS: 'packagePicker:packageFeatures.additionalPhoneNumbers',
                USERS: 'packagePicker:packageFeatures.additionalUsers',
                CHATS: 'packagePicker:packageFeatures.additionalChats',
                CONTACTS: 'packagePicker:packageFeatures.additionalContacts',
                SCRIPTS: 'packagePicker:packageFeatures.additionalScripts'
            }
        };

        const readableValue = (value) => value === 999999 ? "Unlimited" : value;

        const _buildFeatureText = (translationKey, value) => {
            const count = readableValue(value);

            if (count === 'Unlimited') {
                return _i18n.t(`${translationKey}_unlimited`);
            }

            return _i18n.t(translationKey, {count: parseInt(count, 10)});
        };

        const _buildGridCell = (/** @type {any} */ title, /** @type {any} */ value, isFirst = false) => ({
            title,
            value: value ? readableValue(value) : "-",
            cssStyles: isFirst ? 'billing-package-compare__grid-body-cell--first ' : `billing-package-compare__grid-body-cell--${title}`,
        });

        const _buildGridHeader = (title, value, isFirst = false) => ({
            title,
            value,
            cssStyles: isFirst ? 'billing-package-compare__grid-header-column--first' : `billing-package-compare__grid-header-column--${title}`,
        });

        const _buildGridSectionRow = (rowKey) => {
            const sectionLabelCell = {
                title: rowKey,
                value: _i18n.t(`packagePicker:packageGrid.${rowKey}`),
                cssStyles: `billing-package-compare__grid-section-cell billing-package-compare__grid-section-cell--${rowKey}`
            };
            return [sectionLabelCell];
        };

        const _buildGridRow = (rowKey, valueKey) => {
            const labelCell = _buildGridCell(rowKey, _i18n.t(`packagePicker:packageGrid.${rowKey}`), true);
            const valueCells = self.displayPackages().map((pkg) => _buildGridCell(rowKey, pkg[valueKey]));
            return [labelCell].concat(valueCells);
        };

        const _formatGridPriceDisplay = (i18nKey, price, isUSD) => {
            const formattedPrice = isUSD ? _currencyFormatter.formatUSDForDisplay(price) : _currencyFormatter.formatPlanPrice(price);
            return _i18n.t(`packagePicker:packageGrid.${i18nKey}`, {price: formattedPrice});
        };

        const _prepareBillingPackageForDisplay = (billingPackage) => {
            const billingPackagePresentationObject = new BillingPackagePresentationObjectConstructor();
            billingPackagePresentationObject.billingPackageId = billingPackage.billingPackageId;
            billingPackagePresentationObject.packageId = billingPackage.packageId;
            billingPackagePresentationObject.type = billingPackage.type;
            billingPackagePresentationObject.sequence = billingPackage.sequence;
            billingPackagePresentationObject.name = billingPackage.name;
            billingPackagePresentationObject.description = billingPackage.description;
            billingPackagePresentationObject.price = _currencyFormatter.formatPlanPrice(billingPackage.pricePerMonth);
            billingPackagePresentationObject.pricePerMonth = _formatGridPriceDisplay('pricePerMonth', billingPackage.pricePerMonth, true);

            billingPackagePresentationObject.isMostPopular = billingPackage.isMostPopular;
            billingPackagePresentationObject.isOnboardingRequired = billingPackage.isOnboardingRequired;
            billingPackagePresentationObject.isRetired = billingPackage.isRetired;
            billingPackagePresentationObject.isUpgradable = billingPackage.isUpgradable;
            billingPackagePresentationObject.isSignatureService = billingPackage.isSignatureService;

            billingPackagePresentationObject.features = [];

            if (billingPackage.includedChatSessions != null) {
                billingPackagePresentationObject.includedChatSessions = billingPackage.includedChatSessions;
                billingPackagePresentationObject.pricePerAdditionalChatSession = _formatGridPriceDisplay('pricePerChat', billingPackage.pricePerAdditionalChatSession);
                billingPackagePresentationObject.features.push(
                    {text: _buildFeatureText(TRANSLATION_KEYS.INCLUDED.CHATS, billingPackage.includedChatSessions)},
                    {text: _i18n.t(TRANSLATION_KEYS.ADDITIONAL.CHATS, {price: billingPackagePresentationObject.pricePerAdditionalChatSession})}
                );
            }

            if (billingPackage.includedOutboundContacts != null) {
                billingPackagePresentationObject.includedOutboundContacts = billingPackage.includedOutboundContacts;
                billingPackagePresentationObject.pricePerAdditionalOutboundContact = _formatGridPriceDisplay('pricePerContact', billingPackage.pricePerAdditionalOutboundContact);
                billingPackagePresentationObject.features.push(
                    {text: _buildFeatureText(TRANSLATION_KEYS.INCLUDED.CONTACTS, billingPackage.includedOutboundContacts)},
                    {text: _i18n.t(TRANSLATION_KEYS.ADDITIONAL.CONTACTS, {price: billingPackagePresentationObject.pricePerAdditionalOutboundContact})}
                );
            }

            if (billingPackage.includedMinutes != null) {
                billingPackagePresentationObject.includedMinutes = billingPackage.includedMinutes;
                billingPackagePresentationObject.pricePerAdditionalMinute = _formatGridPriceDisplay('pricePerMinute', billingPackage.pricePerAdditionalMinute, true);
                billingPackagePresentationObject.features.push(
                    {text: _buildFeatureText(TRANSLATION_KEYS.INCLUDED.MINUTES, billingPackage.includedMinutes)},
                    {text: _i18n.t(TRANSLATION_KEYS.ADDITIONAL.MINUTES, {price: billingPackagePresentationObject.pricePerAdditionalMinute})}
                );
            }

            billingPackagePresentationObject.includedPhoneNumbers = billingPackage.includedPhoneNumbers;
            billingPackagePresentationObject.pricePerAdditionalPhoneNumber = _formatGridPriceDisplay('pricePerNumber', billingPackage.pricePerAdditionalPhoneNumber);

            billingPackagePresentationObject.includedScripts = billingPackage.includedScripts;
            billingPackagePresentationObject.pricePerAdditionalScript = _formatGridPriceDisplay('pricePerScript', billingPackage.pricePerAdditionalScript);

            billingPackagePresentationObject.includedUsers = billingPackage.includedUsers;
            billingPackagePresentationObject.pricePerAdditionalUser = _formatGridPriceDisplay('pricePerUser', billingPackage.pricePerAdditionalUser);

            billingPackagePresentationObject.outboundCallingSetupFee = _currencyFormatter.formatUSDForDisplay(billingPackage.outboundCallingSetupFee);
            billingPackagePresentationObject.smsSetupFee = _currencyFormatter.formatUSDForDisplay(billingPackage.smsSetupFee);

            billingPackagePresentationObject.isCurrent = ko.pureComputed(() => {
                return self.currentPackageId() === billingPackage.packageId;
            });

            billingPackagePresentationObject.isSelected = ko.pureComputed(() => {
                return self.selectedPackageId() === billingPackage.packageId;
            });

            billingPackagePresentationObject.isSelectable = ko.pureComputed(() => {
                return billingPackage.type !== _billingPackageTypes.telephony.id;
            });

            return billingPackagePresentationObject;
        };

        self.onSelectCallback = null;
        self.onCustomCallback = null;
        self.packageType = ko.observable(null);
        self.availablePackages = ko.observableArray([]);
        self.currentPackageId = ko.observable(null);
        self.selectedPackageId = ko.observable(null);

        self.displayPackages = ko.pureComputed(() => {
            return self.availablePackages()
                .filter((pkg) => {
                    return self.tabActive() === self.tabMetadata.signature.id ? pkg.isSignatureService : !pkg.isSignatureService;
                })
                .sort((a, b) => a.sequence - b.sequence);
        });

        self.showOutboundCallingSetupFee = ko.pureComputed(() => {
            return self.packageType() === _billingPackageTypes.outboundCalling.id;
        });

        self.showTabs = ko.pureComputed(() => {
            const availableSignatureServicePackages = self.availablePackages().filter((pkg) => pkg.type === self.packageType() && pkg.isSignatureService);
            return availableSignatureServicePackages.length > 0;
        });

        self.tabActive = ko.observable();
        self.tabMetadata = {
            classic: {
                id: "classic",
                isValid: ko.observable(true),
                title: _i18n.t('packagePicker:packageTab.classic.label')
            },
            signature: {
                id: "signature",
                isValid: ko.observable(true),
                title: _i18n.t('packagePicker:packageTab.signature.label')
            },
        };
        self.tabs = [
            new TabPresentationObjectConstructor(self.tabMetadata.classic.id, self.tabActive, self.tabMetadata.classic.isValid, self.tabMetadata.classic.title),
            new TabPresentationObjectConstructor(self.tabMetadata.signature.id, self.tabActive, self.tabMetadata.signature.isValid, self.tabMetadata.signature.title)
        ];

        self.tabActiveDescription = ko.pureComputed(() => {
            return self.tabActive() === self.tabMetadata.classic.id ? _i18n.t('packagePicker:packageTab.classic.description') : _i18n.t('packagePicker:packageTab.signature.description');
        });

        self.dataGridTableHeaders = ko.pureComputed(() => {
            if (self.displayPackages().length === 0) {
                return [];
            }

            const labelCell = _buildGridHeader('plan', _i18n.t('packagePicker:packageGrid.plan'), true);
            const packageCells = self.displayPackages().map((pkg) => _buildGridHeader('plan', pkg.name));
            return [labelCell].concat(packageCells);
        });

        self.dataGridTableRows = ko.pureComputed(() => {
            if (self.displayPackages().length === 0) {
                return [];
            }

            switch (self.packageType()) {
                case _billingPackageTypes.telephony.id:
                    return [
                        _buildGridRow(GRID_ROWS.price, 'pricePerMonth'),
                        _buildGridSectionRow(GRID_ROWS.included, 'included'),
                        _buildGridRow(GRID_ROWS.includedMinutes, 'includedMinutes'),
                        _buildGridRow(GRID_ROWS.includedNumbers, 'includedPhoneNumbers'),
                        _buildGridRow(GRID_ROWS.includedUsers, 'includedUsers'),
                        _buildGridSectionRow(GRID_ROWS.extra, 'extra'),
                        _buildGridRow(GRID_ROWS.extraMinute, 'pricePerAdditionalMinute'),
                        _buildGridRow(GRID_ROWS.extraNumber, 'pricePerAdditionalPhoneNumber'),
                        _buildGridRow(GRID_ROWS.extraUser, 'pricePerAdditionalUser')
                    ];
                case _billingPackageTypes.liveReceptionist.id:
                    return [
                        _buildGridRow(GRID_ROWS.price, 'pricePerMonth'),
                        _buildGridSectionRow(GRID_ROWS.included, 'included'),
                        _buildGridRow(GRID_ROWS.includedMinutes, 'includedMinutes'),
                        _buildGridRow(GRID_ROWS.includedNumbers, 'includedPhoneNumbers'),
                        _buildGridRow(GRID_ROWS.includedScripts, 'includedScripts'),
                        _buildGridRow(GRID_ROWS.includedUsers, 'includedUsers'),
                        _buildGridSectionRow(GRID_ROWS.extra, 'extra'),
                        _buildGridRow(GRID_ROWS.extraMinute, 'pricePerAdditionalMinute'),
                        _buildGridRow(GRID_ROWS.extraNumber, 'pricePerAdditionalPhoneNumber'),
                        _buildGridRow(GRID_ROWS.extraScript, 'pricePerAdditionalScript'),
                        _buildGridRow(GRID_ROWS.extraUser, 'pricePerAdditionalUser')
                    ];
                case _billingPackageTypes.webChat.id:
                    return [
                        _buildGridRow(GRID_ROWS.price, 'pricePerMonth'),
                        _buildGridSectionRow(GRID_ROWS.included, 'included'),
                        _buildGridRow(GRID_ROWS.includedChats, 'includedChatSessions'),
                        _buildGridRow(GRID_ROWS.includedNumbers, 'includedPhoneNumbers'),
                        _buildGridRow(GRID_ROWS.includedScripts, 'includedScripts'),
                        _buildGridRow(GRID_ROWS.includedUsers, 'includedUsers'),
                        _buildGridSectionRow(GRID_ROWS.extra, 'extra'),
                        _buildGridRow(GRID_ROWS.extraChat, 'pricePerAdditionalChatSession'),
                        _buildGridRow(GRID_ROWS.extraNumber, 'pricePerAdditionalPhoneNumber'),
                        _buildGridRow(GRID_ROWS.extraScript, 'pricePerAdditionalScript'),
                        _buildGridRow(GRID_ROWS.extraUser, 'pricePerAdditionalUser')
                    ];
                case _billingPackageTypes.outboundCalling.id:
                    return [
                        _buildGridRow(GRID_ROWS.price, 'pricePerMonth'),
                        _buildGridSectionRow(GRID_ROWS.included, 'included'),
                        _buildGridRow(GRID_ROWS.includedContacts, 'includedOutboundContacts'),
                        _buildGridRow(GRID_ROWS.includedNumbers, 'includedPhoneNumbers'),
                        _buildGridRow(GRID_ROWS.includedScripts, 'includedScripts'),
                        _buildGridRow(GRID_ROWS.includedUsers, 'includedUsers'),
                        _buildGridSectionRow(GRID_ROWS.extra, 'extra'),
                        _buildGridRow(GRID_ROWS.extraContact, 'pricePerAdditionalOutboundContact'),
                        _buildGridRow(GRID_ROWS.extraNumber, 'pricePerAdditionalPhoneNumber'),
                        _buildGridRow(GRID_ROWS.extraScript, 'pricePerAdditionalScript'),
                        _buildGridRow(GRID_ROWS.extraUser, 'pricePerAdditionalUser')
                    ];
            }
        });

        self.onNeedLargerPlanSelected = () => {
            if (self.onCustomCallback) {
                self.onCustomCallback();
            }
        };

        self.toggleSelectedPackage = (billingPackage) => {
            self.selectedPackageId(billingPackage.packageId);
            if (self.onSelectCallback) {
                self.onSelectCallback(billingPackage);
            }
        };

        self.activate = ({ packageType, packageId, availablePackages, onSelectCallback = null, onCustomCallback = null}) => {
            return _initialize(packageType, packageId, availablePackages, onSelectCallback, onCustomCallback);
        };

        const _initialize = (packageType, packageId, availablePackages, onSelectCallback, onCustomCallback) => {
            self.packageType(ko.unwrap(packageType));
            self.currentPackageId(ko.unwrap(packageId));

            if (ko.isObservable(packageId)) {
                self.selectedPackageId = packageId;
            } else {
                self.selectedPackageId(packageId);
            }

            self.onSelectCallback = onSelectCallback;
            self.onCustomCallback = onCustomCallback;

            const displayBillingPackages = ko.unwrap(availablePackages).map((p) => _prepareBillingPackageForDisplay(p));
            self.availablePackages(displayBillingPackages);

            self.tabActive(self.tabs[0].id);

            return _promiseFactory.wait();
        };
    };
});
